import { ReactNode, CSSProperties } from 'react';
import { Card as BaseCard } from '@jira-clone/ui';

export interface CardProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  /** Card nested inside another card: border only, no shadow. */
  variant?: 'default' | 'inset';
  /** Hover elevation + pointer cursor. Defaults to true when onClick is set. */
  interactive?: boolean;
}

/**
 * Thin wrapper over the canonical Card in @jira-clone/ui (kept for its
 * Card / CardHeader / CardBody API). Styling lives in one place there.
 */
export function Card({ children, className = '', style, onClick, variant, interactive }: CardProps) {
  return (
    <BaseCard className={className} style={style} onClick={onClick} variant={variant} interactive={interactive ?? !!onClick}>
      {children}
    </BaseCard>
  );
}

export interface CardHeaderProps {
  /** Heading text. Optional so the compositional form works too. */
  title?: ReactNode;
  /** Secondary text under the heading. */
  subtitle?: ReactNode;
  /** Right-aligned control (a button, a menu, a count). */
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
}

export function CardHeader({ title, subtitle, action, children, className = '' }: CardHeaderProps) {
  // Two supported spellings: <CardHeader title=... /> and
  // <CardHeader><h3/></CardHeader>. Whichever is used renders identically.
  return (
    <div className={`flex items-start justify-between gap-3 border-b border-border px-6 py-4 ${className}`}>
      <div className="min-w-0">
        {title !== undefined && <h3 className="text-base font-semibold text-foreground">{title}</h3>}
        {subtitle !== undefined && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
        {children}
      </div>
      {action !== undefined && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export interface CardBodyProps {
  children: ReactNode;
  className?: string;
}

export function CardBody({ children, className = '' }: CardBodyProps) {
  return <div className={`p-6 ${className}`}>{children}</div>;
}
